<template>
  <div v-if="showOffer"
       class="card card-shadow ml-2 mr-2 ml-md-0 mr-md-0"
       :class="color_by_status(status)">

    <div class="card-header">
      <div class="row">
        <div v-if="showSelectOfferColumn" class="align-middle col col-md-1">
          <div v-if="displayRadioButton" class="radio radio-success">
            <input type="radio"
                   :id="offerId"
                   :value="offerId"
                   :checked="offerId == selectedOfferId"
                   @change="$emit('input', offerId)">
            <label :for="offerId"></label>
          </div>

          <div v-else>
            <i class="fa fa-check-circle text-green" aria-hidden="true"></i>
          </div>
        </div>

        <div v-if="!isSingleEntity"
             :data-label="$t('table.headers.entity')"
             class="col-md h6"
             @click="showDetails(offer)">
          <b>{{ entityName }}</b>
        </div>

        <div :data-label="$t('table.headers.offer_begin_datetime')"
             class="col-md"
             @click="showDetails(offer)">
          <div class="description">
            {{ $t('table.headers.offer_begin_datetime') }}
          </div>
          <b>{{ offer.start_at | formatDateAtTimeList }}</b>
        </div>

        <div :data-label="$t('table.headers.offer_end_datetime')"
             class="col-md"
             @click="showDetails(offer)">
          <div class="description">
            {{ $t('table.headers.offer_end_datetime') }}
          </div>
          <b>{{ offer.end_at | formatDateAtTimeList }}</b>
        </div>

        <div class="col-md">
          <div class="btn-toolbar flex-wrap justify-content-end">
            <div class="btn-group" v-if="actionColumn">
              <router-link v-if="!isManager"
                           :to="{ name: 'OfferCandidateDetail', params: { id: offer.id }}"
                           class="btn btn-icon">
                <i class="fa fa-eye"></i>
              </router-link>

              <router-link v-else
                           :to="{ name: 'AdminOffer', params: { id: offer.id }}"
                           class="btn btn-icon">
                <i class="fa fa-eye"></i>
              </router-link>

              <div v-if="canDelete"
                   href="#delete"
                   class="btn btn-icon-dark"
                   @click.prevent="deleteOffer">
                <i class="fa fa-trash-o"></i>
              </div>

              <div v-if="contractEditionButton"
                   href="#select"
                   class="btn btn-icon"
                   @click.prevent="selectContract">
                <i class="fa fa-check text-green" v-if="hasContract"></i>
                <img src="../../assets/img/icons8-contract-100.png" width="22px" height="22px"  alt="" v-else/>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div @click="showDetails(offer)" class="card-body">
      <div class="row">
        <div :data-label="$t('table.headers.services')"
             class="col-md mt-3 mb-3 mt-md-0 mb-md-0">
          <div class="description">{{ $t('table.headers.services') }} </div>
          <services-with-hub v-if="hub" :hub="hub" :servicesNames="services" />
          <b v-else>{{ services }}</b>
        </div>

        <div :data-label="$t('table.headers.occupation')"
             class="col-md mb-3 mb-md-0">
          <div class="description">{{ $t('table.headers.occupation') }} </div>
          <b>{{ occupations }}</b>
        </div>
      </div>

      <div class="row" v-if="showReplacementMotive"
           :data-label="$t('table.headers.replacement_motive')">
        <div class="col-md mt-3 mb-3 mt-md-0 mb-md-0">
          <div class="description">{{ $t('table.headers.replacement_motive') }} </div>
          {{ offer.replacement_motive_name }}
        </div>
      </div>
    </div>

    <div class="card-footer text-muted"
         @click="showDetails(offer)"
         v-if="canShowCandidate || (status!=offerStatus.draft && isManager)"
         >
      <div class="row">
        <div v-if="canShowCandidate"
             :data-label="$t('table.headers.candidate')"
             class="col text-left"
             :class="{ 'cannot-edit-contract': cannotEditContract }">
          <div v-if="offer.status == 'validated'">
            <div class="description">{{ $t('table.headers.candidate') }}</div>
            <b :class="(candidateResourceType == 'intern') ? 'color-intern' : 'color-extern'">{{ candidateFullName }}</b>
          </div>
        </div>

        <div v-if="status!=offerStatus.draft && isManager"
             :data-label="$t('table.headers.published_at')"
             class="col text-right">
          <div class="description">{{ $t('table.headers.published_at') }}</div>
          <b>{{ offer.published_at | formatDateAtTime }}</b>

          <div v-show="publisher" class="description">{{ $t('table.headers.by') }}</div>
          <b v-show="publisher">{{ publisher }}</b>
        </div>
      </div>
    </div>
  </div>
</template>

<script type="text/javascript">
  import { mapGetters } from 'vuex'
  import { offerStatus } from '@/settings/offers.js'
  import { getContractTemplatesByEntityId } from '@/helpers/contracts'
  import { managerHasHubs } from '@/helpers/managers'
  import SelectContractForm from './SelectContractForm'
  import ServicesWithHub from '@/components/hubs/ServicesWithHub'

  const SELECT_CONTRACT_VIEW_NAME = 'SelectContractForm'

  export default {
    components: {
      ServicesWithHub
    },
    props: {
      isManager: Boolean,
      offer: Object,
      status: String,
      isSingleEntity: Boolean,
      contractTemplates: Array,
      canEditContract: { type: Boolean, default: false },
      actionColumn: { type: Boolean, default: true },
      activateShowDetails: { type: Boolean, default: true },
      showOnlySelectedOffers: { type: Boolean, default: false },
      showSelectOfferColumn: { type: Boolean, default: false },
      selectedOfferId: { type: Number, default: null },
      showReplacementMotive: { type: Boolean, default: false },
      showHub: { type: Boolean, default: false }
    },
    computed: {
      ...mapGetters({
        currentUser: 'auth/currentUser',
        selectedTab: 'offers/selectedTab',
        currentPage: 'offers/currentPage',
        selectedOfferIds: 'contracts/selectedOfferIds'
      }),
      occupations() {
        return this.offer.replaced_occupation_name
      },
      services() {
        return this.offer.replaced_service_name
      },
      publisher() {
        return this.offer.publisher_name;
      },
      offerId() {
        return this.offer.id
      },
      entityName() {
        return this.offer.entity.name
      },
      canDelete() {
        return this.$can('delete', this.offer) && this.status != 'passed'
      },
      offerStatus() {
        return offerStatus;
      },
      canShowCandidate() {
        return this.isManager;
      },
      validatedCandidate() {
        return this.offer.validated_candidate;
      },
      candidateFullName(){
        if(this.validatedCandidate) {
          return `${this.validatedCandidate.first_name} ${this.validatedCandidate.last_name}`;
        }
      },
      candidateResourceType(){
        if(this.validatedCandidate) {
          return this.validatedCandidate.resource_type;
        }
      },
      contractEditionButton(){
        if(this.contractEdition()) {
          return this.offer.validated_candidate.resource_type == 'extern'
        }
        return false
      },
      cannotEditContract() {
        if(this.contractEdition()) {
          return this.offer.validated_candidate.resource_type == 'intern'
        }
        return false
      },
      showOffer() {
        return this.showOnlySelectedOffers ? this.isSelectedOffer() : true;
      },
      displayRadioButton() {
        return this.isManager && this.showSelectOfferColumn && !this.isSelectedOffer()
      },
      hasContract() {
        return this.offer.contract != null;
      },
      hubName() {
        return this.offer.hub.is_fake ? '' : this.offer.hub.name;
      },
      hub() {
        return this.offer.hub.is_fake ? null : this.offer.hub;
      }
    },
    methods: {
      showDetails(offer) {
        if(this.activateShowDetails) {
          var admin = { name: 'AdminOffer', params: { id: offer.id } }
          var candidate = { name: 'OfferCandidateDetail', params: { id: offer.id } }
          this.$router.push(this.isManager ? admin: candidate)
        }
      },
      deleteOffer: function(dialog) {
        var self = this
        this.$dialog.confirm(this.$i18n.t('Offer.delete_confirmation'))
        .then(function () {
          self.$store.dispatch('offers/deleteOffer', self.offer.id)
          .then(response => {
            if (response.status == 200){
              let params = { page: self.currentPage };
              if(self.selectedTab != 'all'){ params["status"] = self.selectedTab } ;
              self.$store.dispatch('offers/loadOfferList', params)
              self.$store.dispatch('offers/countByStatus')
            }
          });
        })
        .catch(function () {})
      },
      selectContract() {
        if (this.hasContract) {
          this.$router.push({
            name: 'Contract',
            params: { id: this.offer.contract.id }
          });
        } else {
          this.$dialog.alert("select contract", {
            view: SELECT_CONTRACT_VIEW_NAME,
            html: true,
            animation: 'fade',
            backdropClose: true,
            contractTemplates: this.contractTemplatesForOffer()
          })
          .then(dialog => {
            this.$router.push({ name: 'OfferContract', params: {id: this.offer.id, contract_id: dialog.data}});
          });
        }
      },
      contractTemplatesForOffer() {
        return getContractTemplatesByEntityId({
          entityId: this.offer.entity.id,
          templates: this.contractTemplates
        })
      },
      color_by_status(status){
        if (status == "all") {
          return this.offer.status + "-border-color"
        } else if (status == "expired") {
          if (this.offer.status == "validated") {
            return 'expired_and_validated-border-color'
          } else if (this.offer.status == "cancelled") {
            return 'cancelled-border-color'
          } else {
            return 'expired_and_not_validated-border-color'
          }
        }
        return status + '-border-color'
      },
      contractEdition() {
        return this.isManager &&
          (this.status == "validated" || this.status == "expired_and_validated") &&
          this.canEditContract &&
          this.offer.validated_candidate != undefined;
      },
      isSelectedOffer() {
        return this.selectedOfferIds.includes(this.offer.id);
      },
      loadDialogSelectContractForm() {
        this.$dialog.registerComponent(SELECT_CONTRACT_VIEW_NAME, SelectContractForm);
      }
    },
    mounted: function() {
      this.loadDialogSelectContractForm()
    }
  }
</script>

<style lang="scss" type="text/css" scoped>
  @import '../../assets/scss/pages/_var.scss';
  td:not(:last-child) {
    cursor: pointer
  }

  tr{

    border-bottom: 2px solid white;
  }
  .select-offer {
    border-style: hidden;
  }

  .radio, .radio label {
    margin: 0px;
    padding: 0px;
  }
  .fa-check-circle {
    font-size: 20px;
  }

  tr td:first-child {
    border-left: 5px solid $color-all;
  }

  .published-border-color{
    border-left: 6px solid $color-published;
  }
  .waiting-border-color{
    border-left: 6px solid $color-waiting;
  }
  .validated-border-color, .expired_and_validated-border-color{
    border-left: 6px solid $color-validated;
  }
  .draft-border-color{
    border-left: 6px solid $color-draft;
  }
  .expired_and_not_validated-border-color{
    border-left: 6px solid $color-expired;
  }
  .cancelled-border-color{
    border-left: 6px solid $color-cancelled;
  }
  .cannot-edit-contract{
    color: #b0abab;
  }
  .card-footer .col div {
    display: inline-block;
  }
  .card-footer {
    background: none;
    border-top: none;
    padding-top: 0;
    padding-bottom: 10px;
  }
</style>



// WEBPACK FOOTER //
// src/components/offers/OfferRow.vue